import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";
import { AppThunk } from "state";
import Parse from "parse";
import { loggedCloudRun, hasActiveMainRequests } from "../utils/apiLogger";

type CacheEntry = {
  data: any;
  timestamp: number;
  branchIds: string[];
};

type State = {
  startDate: number | null;
  endDate: number | null;
  ageRange: string | null;
  genderFilter: string | null;
  ordersFilter: string | null;
  branchFilter: string[] | null;
  loading: boolean;
  branches: Array<any>;
  selectedDateOption: string;
  currentBranchId: string | null;
  currentBranchName: string | null;
  allBranchIds: string[] | null;
  selectedBranchType: "single" | "all" | null;
  dataCache: Record<string, CacheEntry>;
};

const initialState: State = {
  startDate: moment().startOf("month").valueOf(),
  endDate: moment().endOf("month").valueOf(),
  ageRange: null,
  genderFilter: null,
  ordersFilter: "Dine-in",
  branchFilter: [],
  loading: false,
  branches: [],
  selectedDateOption: "Last 30 Days",
  currentBranchId: null,
  currentBranchName: null,
  allBranchIds: null,
  selectedBranchType: null,
  dataCache: {},
};

const dashboardFiltersSlice = createSlice({
  name: "dashboardFilters",
  initialState,
  reducers: {
    updateDatePicker(
      state,
      action: PayloadAction<{
        startDate: number | null;
        endDate: number | null;
        selectedDateOption: string;
      }>,
    ) {
      state.startDate = action.payload.startDate;
      state.endDate = action.payload.endDate;
      state.selectedDateOption = action.payload.selectedDateOption;
    },
    updateAgeRange(state, action: PayloadAction<{ ageRange: string | null }>) {
      state.ageRange = action.payload.ageRange;
    },
    updateGenderFilter(
      state,
      action: PayloadAction<{ genderFilter: string | null }>,
    ) {
      state.genderFilter = action.payload.genderFilter;
    },
    updateOrdersFilter(
      state,
      action: PayloadAction<{ ordersFilter: string | null }>,
    ) {
      state.ordersFilter = action.payload.ordersFilter;
    },
    updateBranchFilter(
      state,
      action: PayloadAction<{ branchFilter: string[] }>,
    ) {
      // Create a new array to ensure mutability
      state.branchFilter = Array.from(action.payload.branchFilter);
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    fetchBranchesSuccess(state, action: PayloadAction<Array<any>>) {
      state.branches = action.payload;
    },
    setCurrentBranch(
      state,
      action: PayloadAction<{ id: string; name: string }>,
    ) {
      state.currentBranchId = action.payload.id;
      state.currentBranchName = action.payload.name;
    },
    setAllBranchIds(state, action: PayloadAction<string[]>) {
      state.allBranchIds = action.payload;
    },
    setSelectedBranchType(
      state,
      action: PayloadAction<"single" | "all" | null>,
    ) {
      state.selectedBranchType = action.payload;
    },
    setCacheEntry(
      state,
      action: PayloadAction<{
        key: string;
        data: any;
        branchIds: string[];
      }>,
    ) {
      if (!state.dataCache) {
        state.dataCache = {};
      }
      state.dataCache[action.payload.key] = {
        data: action.payload.data,
        timestamp: Date.now(),
        branchIds: action.payload.branchIds,
      };
    },
    clearCache(state) {
      state.dataCache = {};
    },
    resetFormDashboardFilters(state) {
      Object.assign(state, initialState);
    },
  },
});

export const {
  updateDatePicker,
  updateAgeRange,
  updateBranchFilter,
  updateGenderFilter,
  updateOrdersFilter,
  setLoading,
  fetchBranchesSuccess,
  resetFormDashboardFilters,
  setCurrentBranch,
  setAllBranchIds,
  setSelectedBranchType,
  setCacheEntry,
  clearCache,
} = dashboardFiltersSlice.actions;

// Cache utility functions
export const getCacheKey = (
  endpoint: string,
  branchIds: string[],
  startDate: number,
  endDate: number,
): string => {
  if (!branchIds || branchIds.length === 0) {
    return `${endpoint}_[]_${startDate}_${endDate}`;
  }
  // Create a mutable copy by converting to array explicitly
  const sortedIds = Array.from(branchIds).sort();
  return `${endpoint}_${sortedIds.join(",")}_${startDate}_${endDate}`;
};

export const isCacheValid = (entry: CacheEntry | undefined): boolean => {
  if (!entry) return false;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  return Date.now() - entry.timestamp < CACHE_DURATION;
};

export default dashboardFiltersSlice.reducer;

export const updateFilters =
  (
    startDate: moment.Moment | null,
    endDate: moment.Moment | null,
    ageRange: string | null = null,
    genderFilter: string | null = null,
    ordersFilter: string | null = null,
    branchFilter: string[] = [],
    selectedDateOption: string,
  ): AppThunk =>
  async (dispatch) => {
    dispatch(setLoading(true));

    // Apply the filters and update the state
    dispatch(
      updateDatePicker({
        startDate: startDate ? startDate.valueOf() : null,
        endDate: endDate ? endDate.valueOf() : null,
        selectedDateOption,
      }),
    );
    if (ageRange !== null) dispatch(updateAgeRange({ ageRange }));
    if (genderFilter !== null) dispatch(updateGenderFilter({ genderFilter }));
    if (branchFilter !== null) dispatch(updateBranchFilter({ branchFilter }));
    if (ordersFilter !== null) dispatch(updateOrdersFilter({ ordersFilter }));

    // End loading after updates
    dispatch(setLoading(false));
  };

export const fetchBranches = (): AppThunk => async (dispatch, getState) => {
  try {
    // Check if branches are already loaded to avoid unnecessary API calls
    const currentState = getState();
    if (
      currentState.dashboardFilters.branches.length > 0 &&
      currentState.dashboardFilters.allBranchIds &&
      currentState.dashboardFilters.allBranchIds.length > 0
    ) {
      console.log(
        "✅ [fetchBranches] Branches already loaded, skipping API call",
      );
      return;
    }

    const currentUser = Parse.User.current();
    if (!currentUser) throw new Error("User not logged in");

    // Get current entity ID
    const entityPointer = currentUser.get("entity");
    if (!entityPointer) throw new Error("Entity not found");

    const currentEntityId = entityPointer.id;
    if (!currentEntityId) throw new Error("Entity ID not found");

    console.log(
      "📤 [fetchBranches] Calling getBranches API with entityId:",
      currentEntityId,
    );
    // Call getBranches API with entityId
    const result = await loggedCloudRun(
      "getBranches",
      {
        entityId: currentEntityId,
      },
      {
        type: "main",
        context: "fetchBranches",
      },
    );

    if (!result.success || !result.data) {
      throw new Error(result.error || "Failed to fetch branches");
    }

    const { branches, branchIds } = result.data;

    // Set current branch and default filter only on initial load (if not already set)
    const state = getState();
    if (branches && branches.length > 0) {
      // Use the first branch as default if no branch is currently selected
      const firstBranch = branches[0];
      const firstBranchId = firstBranch.id;

      // Only set current branch on initial load if it's not already set by user selection
      // This prevents resetting the branch name when user has manually selected a different branch
      if (!state.dashboardFilters.currentBranchId) {
        dispatch(
          setCurrentBranch({
            id: firstBranchId,
            name: firstBranch.name || "Branch Name",
          }),
        );
        dispatch(setSelectedBranchType("single"));
      }
      // Set first branch as default filter if none is set
      if (
        !state.dashboardFilters.branchFilter ||
        state.dashboardFilters.branchFilter.length === 0
      ) {
        dispatch(updateBranchFilter({ branchFilter: [firstBranchId] }));
      }
    }

    // Store all branch IDs for prefetching
    dispatch(setAllBranchIds(branchIds || []));

    // Store branches for dropdown
    const serializedBranches = branches.map((branch: any) => ({
      objectId: branch.id,
      name: branch.name,
      id: branch.id,
    }));

    dispatch(fetchBranchesSuccess(serializedBranches));
  } catch (error) {
    console.error("Failed to fetch branches:", error);
    dispatch(setLoading(false));
  }
};

// Background prefetch function for all branches with caching
// Optimized to only prefetch what's not cached and limit concurrent requests
export const prefetchAllBranchesData =
  (
    branchIds: string[],
    startDate: number,
    endDate: number,
    isCustomersPage: boolean = false,
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      const state = getState();

      // Store functions that create promises (lazy execution)
      const prefetchTasks: Array<() => Promise<any>> = [];

      // Get currently selected branch to skip prefetching it (already loaded)
      const currentBranchFilter = state.dashboardFilters.branchFilter || [];
      const currentlySelectedBranch =
        currentBranchFilter.length === 1 ? currentBranchFilter[0] : null;

      // Limit prefetch to first 3 branches (excluding currently selected) to avoid overwhelming backend
      const MAX_PREFETCH_BRANCHES = 3;
      const branchesToPrefetch = branchIds
        .filter((id) => id !== currentlySelectedBranch)
        .slice(0, MAX_PREFETCH_BRANCHES);

      console.log(
        `🔄 [Prefetch] Limiting prefetch to ${branchesToPrefetch.length} branches (out of ${branchIds.length} total)`,
      );

      // Only prefetch for branches that don't have complete cache
      // Skip the currently selected branch (already loaded)
      branchesToPrefetch.forEach((branchId) => {
        // Skip prefetching the currently selected branch
        if (currentlySelectedBranch === branchId) {
          return;
        }
        const basePayload = {
          restaurantIds: [branchId],
          startDate: startDateObj.toISOString(),
          endDate: endDateObj.toISOString(),
          timezone: "Africa/Cairo",
        };

        // Check cache before prefetching - use split endpoints
        const metricsCacheKey = getCacheKey(
          "getDashboardMetrics",
          [branchId],
          startDate,
          endDate,
        );
        const revenueCacheKey = getCacheKey(
          "getDashboardRevenueOverTime",
          [branchId],
          startDate,
          endDate,
        );
        const tablesOrOrderCacheKey = getCacheKey(
          "getDashboardTablesOrOrderTime",
          [branchId],
          startDate,
          endDate,
        );
        const topItemsCacheKey = getCacheKey(
          "getTopSellingItem",
          [branchId],
          startDate,
          endDate,
        );

        const cachedMetrics =
          state.dashboardFilters.dataCache?.[metricsCacheKey];
        const cachedRevenue =
          state.dashboardFilters.dataCache?.[revenueCacheKey];
        const cachedTablesOrOrder =
          state.dashboardFilters.dataCache?.[tablesOrOrderCacheKey];
        const cachedTopItems =
          state.dashboardFilters.dataCache?.[topItemsCacheKey];

        // Skip this branch if all data is already cached
        const allCached =
          isCacheValid(cachedMetrics) &&
          isCacheValid(cachedRevenue) &&
          isCacheValid(cachedTablesOrOrder) &&
          isCacheValid(cachedTopItems);

        if (allCached) {
          return; // All data cached, skip prefetch for this branch
        }

        // Prefetch metrics if not cached
        if (!isCacheValid(cachedMetrics)) {
          prefetchTasks.push(() =>
            loggedCloudRun("getDashboardMetrics", basePayload, {
              type: "prefetch",
              context: `prefetch-branch-${branchId}-metrics`,
            })
              .then((result) => {
                if (result.success) {
                  dispatch(
                    setCacheEntry({
                      key: metricsCacheKey,
                      data: { metrics: result.data },
                      branchIds: [branchId],
                    }),
                  );
                }
              })
              .catch((err) => {
                console.warn(
                  `Prefetch metrics failed for branch ${branchId}:`,
                  err,
                );
              }),
          );
        }

        // Prefetch revenue over time if not cached
        if (!isCacheValid(cachedRevenue)) {
          prefetchTasks.push(() =>
            loggedCloudRun("getDashboardRevenueOverTime", basePayload, {
              type: "prefetch",
              context: `prefetch-branch-${branchId}-revenue`,
            })
              .then((result) => {
                if (result.success) {
                  dispatch(
                    setCacheEntry({
                      key: revenueCacheKey,
                      data: { revenue: result.data },
                      branchIds: [branchId],
                    }),
                  );
                }
              })
              .catch((err) => {
                console.warn(
                  `Prefetch revenue over time failed for branch ${branchId}:`,
                  err,
                );
              }),
          );
        }

        // Prefetch tables/order time if not cached
        if (!isCacheValid(cachedTablesOrOrder)) {
          prefetchTasks.push(() =>
            loggedCloudRun("getDashboardTablesOrOrderTime", basePayload, {
              type: "prefetch",
              context: `prefetch-branch-${branchId}-tablesOrOrder`,
            })
              .then((result) => {
                if (result.success) {
                  dispatch(
                    setCacheEntry({
                      key: tablesOrOrderCacheKey,
                      data: { tablesOrOrder: result.data },
                      branchIds: [branchId],
                    }),
                  );
                }
              })
              .catch((err) => {
                console.warn(
                  `Prefetch tables/order time failed for branch ${branchId}:`,
                  err,
                );
              }),
          );
        }

        // Prefetch top items if not cached
        if (!isCacheValid(cachedTopItems)) {
          const topItemsPayload = {
            ...basePayload,
            limit: 5,
          };
          prefetchTasks.push(() =>
            loggedCloudRun("getTopSellingItem", topItemsPayload, {
              type: "prefetch",
              context: `prefetch-branch-${branchId}-topItems`,
            })
              .then((result) => {
                // Normalize response - handle various structures
                let items: any[] = [];
                if (result) {
                  if (Array.isArray(result)) {
                    items = result;
                  } else if (result.success && result.data) {
                    items =
                      result.data.topSellingItems ||
                      result.data.topItems ||
                      result.data.items ||
                      result.data.data ||
                      (Array.isArray(result.data) ? result.data : []);
                    items = Array.isArray(items) ? items : [];
                  } else if (result.success && Array.isArray(result.data)) {
                    items = result.data;
                  }
                }

                // Always cache, even if empty (empty arrays are valid responses)
                dispatch(
                  setCacheEntry({
                    key: topItemsCacheKey,
                    data: { topSellingItems: items },
                    branchIds: [branchId],
                  }),
                );
              })
              .catch((err) => {
                console.warn(
                  `Prefetch top items failed for branch ${branchId}:`,
                  err,
                );
              }),
          );
        }
      });

      // Prefetch "All" data (all branches combined) - only if not cached
      // Skip "All" prefetch on initial load to reduce requests
      if (branchIds.length > 1 && false) {
        // Disabled for now to reduce initial load
        const allPayload = {
          restaurantIds: branchIds,
          startDate: startDateObj.toISOString(),
          endDate: endDateObj.toISOString(),
          timezone: "Africa/Cairo",
        };

        const allMetricsCacheKey = getCacheKey(
          "getDashboardMetrics",
          branchIds,
          startDate,
          endDate,
        );
        const allRevenueCacheKey = getCacheKey(
          "getDashboardRevenueOverTime",
          branchIds,
          startDate,
          endDate,
        );
        const allTablesOrOrderCacheKey = getCacheKey(
          "getDashboardTablesOrOrderTime",
          branchIds,
          startDate,
          endDate,
        );
        const allCustomersCacheKey = getCacheKey(
          "getDashboardCustomers",
          branchIds,
          startDate,
          endDate,
        );
        const allTopItemsCacheKey = getCacheKey(
          "getTopSellingItem",
          branchIds,
          startDate,
          endDate,
        );

        const cachedAllMetrics =
          state.dashboardFilters.dataCache?.[allMetricsCacheKey];
        const cachedAllRevenue =
          state.dashboardFilters.dataCache?.[allRevenueCacheKey];
        const cachedAllTablesOrOrder =
          state.dashboardFilters.dataCache?.[allTablesOrOrderCacheKey];
        const cachedAllCustomers =
          state.dashboardFilters.dataCache?.[allCustomersCacheKey];
        const cachedAllTopItems =
          state.dashboardFilters.dataCache?.[allTopItemsCacheKey];

        // Prefetch "All" metrics if not cached
        if (!isCacheValid(cachedAllMetrics)) {
          prefetchTasks.push(() =>
            loggedCloudRun("getDashboardMetrics", allPayload, {
              type: "prefetch",
              context: "prefetch-all-branches-metrics",
            })
              .then((result) => {
                if (result.success) {
                  dispatch(
                    setCacheEntry({
                      key: allMetricsCacheKey,
                      data: { metrics: result.data },
                      branchIds: branchIds,
                    }),
                  );
                }
              })
              .catch((err) => {
                console.warn("Prefetch 'All' metrics failed:", err);
              }),
          );
        }

        // Prefetch "All" revenue over time if not cached
        if (!isCacheValid(cachedAllRevenue)) {
          prefetchTasks.push(() =>
            loggedCloudRun("getDashboardRevenueOverTime", allPayload, {
              type: "prefetch",
              context: "prefetch-all-branches-revenue",
            })
              .then((result) => {
                if (result.success) {
                  dispatch(
                    setCacheEntry({
                      key: allRevenueCacheKey,
                      data: { revenue: result.data },
                      branchIds: branchIds,
                    }),
                  );
                }
              })
              .catch((err) => {
                console.warn("Prefetch 'All' revenue over time failed:", err);
              }),
          );
        }

        // Prefetch "All" tables/order time if not cached
        if (!isCacheValid(cachedAllTablesOrOrder)) {
          prefetchTasks.push(() =>
            loggedCloudRun("getDashboardTablesOrOrderTime", allPayload, {
              type: "prefetch",
              context: "prefetch-all-branches-tablesOrOrder",
            })
              .then((result) => {
                if (result.success) {
                  dispatch(
                    setCacheEntry({
                      key: allTablesOrOrderCacheKey,
                      data: { tablesOrOrder: result.data },
                      branchIds: branchIds,
                    }),
                  );
                }
              })
              .catch((err) => {
                console.warn("Prefetch 'All' tables/order time failed:", err);
              }),
          );
        }

        // Prefetch "All" customers if not cached
        if (!isCacheValid(cachedAllCustomers)) {
          prefetchTasks.push(() =>
            loggedCloudRun("getDashboardCustomers", allPayload, {
              type: "prefetch",
              context: "prefetch-all-branches-customers",
            })
              .then((result) => {
                if (result.success) {
                  dispatch(
                    setCacheEntry({
                      key: allCustomersCacheKey,
                      data: { customers: result.data },
                      branchIds: branchIds,
                    }),
                  );
                }
              })
              .catch((err) => {
                console.warn("Prefetch 'All' customers failed:", err);
              }),
          );
        }

        // Prefetch "All" top items if not cached
        if (!isCacheValid(cachedAllTopItems)) {
          const allTopItemsPayload = {
            ...allPayload,
            limit: 5,
          };
          prefetchTasks.push(() =>
            loggedCloudRun("getTopSellingItem", allTopItemsPayload, {
              type: "prefetch",
              context: "prefetch-all-branches-topItems",
            })
              .then((result) => {
                // Normalize response - handle various structures
                let items: any[] = [];
                if (result) {
                  if (Array.isArray(result)) {
                    items = result;
                  } else if (result.success && result.data) {
                    items =
                      result.data.topSellingItems ||
                      result.data.topItems ||
                      result.data.items ||
                      result.data.data ||
                      (Array.isArray(result.data) ? result.data : []);
                    items = Array.isArray(items) ? items : [];
                  } else if (result.success && Array.isArray(result.data)) {
                    items = result.data;
                  }
                }

                // Always cache, even if empty (empty arrays are valid responses)
                dispatch(
                  setCacheEntry({
                    key: allTopItemsCacheKey,
                    data: { topSellingItems: items },
                    branchIds: branchIds,
                  }),
                );
              })
              .catch((err) => {
                console.warn("Prefetch 'All' top items failed:", err);
              }),
          );
        }
      }

      // Run all prefetches in background without blocking
      // Process in batches to avoid overwhelming the server
      if (prefetchTasks.length > 0) {
        console.log(
          `🔄 [Prefetch] Starting ${prefetchTasks.length} background prefetch requests (batched, max 3 branches)...`,
        );
        // Process in batches to limit concurrent requests
        const BATCH_SIZE = 2; // Max 2 concurrent requests at a time (reduced further)
        const BATCH_DELAY = 2000; // 2 second delay between batches (increased to reduce load)
        const MAIN_REQUEST_CHECK_DELAY = 500; // Check every 500ms if main requests are active

        // Store timeout IDs so we can track them
        const timeoutIds: NodeJS.Timeout[] = [];

        for (let i = 0; i < prefetchTasks.length; i += BATCH_SIZE) {
          const batch = prefetchTasks.slice(i, i + BATCH_SIZE);
          const batchNum = Math.floor(i / BATCH_SIZE) + 1;

          // Execute batch with delay between batches - create promises only when batch executes
          const timeoutId = setTimeout(() => {
            // Check if main requests are active before executing prefetch batch
            if (hasActiveMainRequests()) {
              console.log(
                `⏸️ [Prefetch] Pausing batch ${batchNum} - main requests active, will retry in ${MAIN_REQUEST_CHECK_DELAY}ms...`,
              );
              // Retry after a short delay
              const retryTimeoutId = setTimeout(() => {
                // Check again
                if (hasActiveMainRequests()) {
                  console.log(
                    `⏸️ [Prefetch] Still pausing batch ${batchNum} - main requests still active, will retry again...`,
                  );
                  // Retry one more time after another delay
                  setTimeout(() => {
                    if (!hasActiveMainRequests()) {
                      console.log(
                        `▶️ [Prefetch] Resuming batch ${batchNum} (${batch.length} requests)...`,
                      );
                      const batchPromises = batch.map((task) => task());
                      Promise.all(batchPromises).catch((err) => {
                        console.warn(
                          `Background prefetch batch ${batchNum} error:`,
                          err,
                        );
                      });
                    } else {
                      console.log(
                        `⏭️ [Prefetch] Skipping batch ${batchNum} - main requests still active`,
                      );
                    }
                  }, MAIN_REQUEST_CHECK_DELAY);
                } else {
                  console.log(
                    `▶️ [Prefetch] Resuming batch ${batchNum} (${batch.length} requests)...`,
                  );
                  const batchPromises = batch.map((task) => task());
                  Promise.all(batchPromises).catch((err) => {
                    console.warn(
                      `Background prefetch batch ${batchNum} error:`,
                      err,
                    );
                  });
                }
              }, MAIN_REQUEST_CHECK_DELAY);
              timeoutIds.push(retryTimeoutId);
            } else {
              console.log(
                `🔄 [Prefetch] Executing batch ${batchNum} (${batch.length} requests)...`,
              );
              // Now create and execute the promises for this batch
              const batchPromises = batch.map((task) => task());
              Promise.all(batchPromises).catch((err) => {
                console.warn(
                  `Background prefetch batch ${batchNum} error:`,
                  err,
                );
              });
            }
          }, batchNum * BATCH_DELAY); // Stagger batches by 2 seconds each
          timeoutIds.push(timeoutId);
        }
      } else {
        console.log("✅ [Prefetch] All data already cached, skipping prefetch");
      }
    } catch (error) {
      console.warn("Prefetch error:", error);
    }
  };
